import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, ShoppingCart, Heart, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import TextDecode from "./TextDecode";
import Logo from "./Logo";

function Navbar() {
  const location = useLocation();
  const isAdmin = location.pathname === "/admin";
  const { cartCount, toggleCart } = useCart();
  const { wishlist, toggleWishlist } = useWishlist();
  const { user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    function handleScroll() {
      // 40px corresponds to the h-10 of the NewsTicker
      setIsScrolled(window.scrollY > 40);
    }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  async function handleLogout() {
    try {
      await logout();
      window.location.href = "/";
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <nav
      className={`bg-black/90 backdrop-blur-xl border-b border-white/5 fixed ${
        isScrolled ? "top-0" : "top-[40px]"
      } left-0 right-0 w-full z-50 transition-all duration-300`}
    >
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-2 lg:grid-cols-3 items-center h-20">
          {/* Left Spacer (Desktop only) */}
          <div className="hidden lg:flex items-center">
            {/* Espacio reservado para balance visual o futuros enlaces */}
          </div>

          {/* Center: Logo & Tagline */}
          <Link
            to="/"
            className="flex items-center justify-start lg:justify-center group"
          >
            <div className="flex flex-col items-start lg:items-center">
              <Logo className="" />
              <span className="text-[10px] font-black uppercase tracking-widest text-white/30 glitch-hover mt-1">
                Soluciones <span className="text-red-500">Informáticas</span>
              </span>
            </div>
          </Link>

          {/* Right: Actions */}
          <div className="flex items-center justify-end gap-2 sm:gap-4">
            {user && (
              <button
                onClick={handleLogout}
                className="p-3 text-white/30 hover:text-red-500 hover:bg-white/5 rounded-2xl transition-all"
                title="Cerrar Sesión"
                aria-label="Cerrar Sesión"
              >
                <LogOut size={20} />
              </button>
            )}
            {!isAdmin && (
              <>
                <button
                  onClick={toggleWishlist}
                  className="group relative p-3 text-white/30 hover:text-red-500 transition-all hover:bg-white/5 rounded-2xl border border-transparent hover:border-white/10"
                  aria-label="Ver lista de deseos"
                >
                  <Heart
                    size={20}
                    className="group-hover:scale-110 transition-transform"
                  />
                  {wishlist.length > 0 && (
                    <span className="absolute top-2 right-2 bg-red-600 text-white text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-lg shadow-lg shadow-red-600/40 animate-bounce">
                      {wishlist.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={toggleCart}
                  className="group relative p-3 text-white/30 hover:text-red-500 transition-all hover:bg-white/5 rounded-2xl border border-transparent hover:border-white/10"
                  aria-label="Ver carrito"
                >
                  <ShoppingCart
                    size={20}
                    className="group-hover:scale-110 transition-transform"
                  />
                  {cartCount > 0 && (
                    <span className="absolute top-2 right-2 bg-red-600 text-white text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-lg shadow-lg shadow-red-600/40 animate-bounce">
                      {cartCount}
                    </span>
                  )}
                </button>
                <Link
                  to="/admin"
                  className="flex items-center justify-center p-3 rounded-2xl text-white/40 hover:text-white hover:bg-white/5 border border-white/5 hover:border-red-600/50 transition-all shadow-2xl group"
                  title="Acceso Administración"
                  aria-label="Ir al panel de administración"
                >
                  <ShieldCheck
                    size={20}
                    className="text-red-600 group-hover:scale-110 transition-transform"
                  />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
